<script setup>
// The real membership components, running on demo data, inside
// /features/memberships. Two views: what a player sees (the club page's list
// of memberships and passes with the pass offered on a meet under it, their
// own memberships on their profile and the "Membership or pass needed" step on
// an invite page) and what the club sees (the applications waiting and the
// Memberships & passes table). Every row is
// the component the product draws; utils/membershipDemo feeds it.
import {computed, ref} from 'vue';
import TlDialog from '~/components/TlDialog.vue';
import TlIconBtn from '~/components/admin/TlIconBtn.vue';
import MembershipTypesTable from '~/components/admin/memberships/MembershipTypesTable.vue';
import MembershipGateCard from '~/components/memberships/MembershipGateCard.vue';
import MembershipRow from '~/components/memberships/MembershipRow.vue';
import MembershipTypeRow from '~/components/memberships/MembershipTypeRow.vue';
import MeetPassOffer from '~/components/memberships/MeetPassOffer.vue';
import {DISPLAY_AS, membershipStatusChip} from '~/utils/memberships';
import {
  MEMBERSHIP_DEMO_CLUB, membershipDemoAdminTypes, membershipDemoApplications, membershipDemoGate, membershipDemoMine, membershipDemoPassOffer, membershipDemoTypes,
} from '~/utils/membershipDemo';

const {setAlertMessage} = useAlertService();

const view = ref('players'); // players | club
const memberships = computed(() => membershipDemoTypes.filter((t) => t.displayAs !== DISPLAY_AS.pass));
const passes = computed(() => membershipDemoTypes.filter((t) => t.displayAs === DISPLAY_AS.pass));
// "Yours" on the club page's row for a type the person holds (the type's id against the membership's membershipTypeId).
const heldTypeIds = computed(() => new Set(membershipDemoMine.map((m) => m.membershipTypeId)));
const tagFor = (t) => (heldTypeIds.value.has(t.id) ? 'Yours' : null);

// A row of Yours opens the membership window: the window's parts, with a live Renew automatically switch. A cancelled
// one (it ends with the term) offers Resume, as the real window does; one someone else pays for has no "ask someone".
const open = ref(false);
const picked = ref(null);
const renew = ref(true);
const openDetail = (m) => {
  picked.value = m;
  renew.value = !m.cancelAtPeriodEnd;
  open.value = true;
};
const pickedIsPass = computed(() => picked.value?.displayAs === DISPLAY_AS.pass);
const pickedChip = computed(() => membershipStatusChip(picked.value ? {...picked.value, cancelAtPeriodEnd: !renew.value} : null));
const noun = computed(() => (pickedIsPass.value ? 'pass' : 'membership'));
const cancelled = computed(() => !renew.value);
const paidByOther = computed(() => !!picked.value?.payerName);
const resume = () => { renew.value = true; setAlertMessage('Resumed. It renews as before.'); };

const waiting = computed(() => membershipDemoApplications.length);
const demoOnly = () => setAlertMessage({message: 'This is a demo: the real page does it.', color: 'info'});
// The admin page's own ⋮ items and icons (TypeStatusMenu / rowActions), inert here.
const MENU = [
  {title: 'Edit', icon: 'mdi-pencil'},
  {title: 'Make it draft', icon: 'mdi-file-outline'},
  {title: 'Make it live, hidden', icon: 'mdi-eye-off-outline'},
  {title: 'Make it closed to new', icon: 'mdi-lock-outline'},
];
</script>

<template>
  <div class="mds">
    <div class="mds-controls">
      <div class="tl-seg" role="group" aria-label="Who is looking">
        <button type="button" class="tl-seg__opt" :class="{'tl-seg__opt--active': view === 'players'}" @click="view = 'players'">What players see</button>
        <button type="button" class="tl-seg__opt" :class="{'tl-seg__opt--active': view === 'club'}" @click="view = 'club'">What the club sees</button>
      </div>
      <span class="mds-hint">{{ view === 'players' ? 'The club page, a profile, an invite page and a meet, for a made-up club.' : 'Memberships & passes in the club\'s admin area. Try the pencils and the menus.' }}</span>
    </div>

    <!-- ===== PLAYERS ===== -->
    <div v-if="view === 'players'" class="mds-grid">
      <section class="mds-col">
        <div class="mds-frame tl-card">
          <div class="mds-frame-head">
            <span class="mds-frame-title">{{ MEMBERSHIP_DEMO_CLUB.name }}</span>
            <span class="mds-frame-sub">Memberships &amp; passes · the club page</span>
          </div>
          <div class="tl-label mds-label">Memberships</div>
          <div class="mds-list">
            <membership-type-row v-for="t in memberships" :key="t.id" :type="t" :tag="tagFor(t)"/>
          </div>
          <div class="tl-label mds-label">Passes</div>
          <div class="mds-list">
            <membership-type-row v-for="t in passes" :key="t.id" :type="t" :tag="tagFor(t)"/>
          </div>
        </div>

        <div class="mds-frame tl-card mds-inert">
          <div class="mds-frame-head">
            <span class="mds-frame-title">Wednesday Night Mixer · tonight</span>
            <span class="mds-frame-sub">Beside Count me in · £8.00 a session</span>
          </div>
          <meet-pass-offer :offer="membershipDemoPassOffer"/>
        </div>
      </section>

      <section class="mds-col">
        <div class="mds-frame tl-card">
          <div class="mds-frame-head">
            <span class="mds-frame-title">Your memberships &amp; passes</span>
            <span class="mds-frame-sub">Profile · open one</span>
          </div>
          <div class="tl-label mds-label">Yours</div>
          <div class="mds-list">
            <membership-row v-for="m in membershipDemoMine" :key="m.id" :membership="m" @open="openDetail(m)"/>
          </div>
        </div>

        <div class="mds-frame tl-card mds-inert">
          <div class="mds-frame-head">
            <span class="mds-frame-title">Club Championship · invite page</span>
            <span class="mds-frame-sub">A ladder that needs a membership</span>
          </div>
          <membership-gate-card :rule="membershipDemoGate" entity-name="Club Championship"/>
        </div>
      </section>
    </div>

    <!-- ===== THE CLUB ===== -->
    <div v-else class="mds-admin">
      <div class="mds-waiting tl-card">
        <div class="mds-waiting-ic"><v-icon size="20" icon="mdi-clock-outline"/></div>
        <div class="flex-fill">
          <div class="mds-waiting-title">{{ waiting }} applications waiting</div>
          <div class="mds-waiting-sub">Under-18s reach you once a parent or carer has agreed. The nearest card hold runs out in 3 days.</div>
        </div>
        <v-btn color="primary" rounded="pill" size="small" @click="demoOnly">Review</v-btn>
      </div>
      <div class="mds-apps tl-card">
        <div v-for="a in membershipDemoApplications" :key="a.id" class="mds-app">
          <div class="mds-app-ic"><v-icon size="16" icon="mdi-account-outline"/></div>
          <div class="flex-fill">
            <div class="mds-app-title">{{ a.name }} · {{ a.type }}</div>
            <div class="mds-app-sub">{{ a.price }} · {{ a.line }} · {{ a.when }}</div>
          </div>
          <span class="tl-chip tl-chip--warn">{{ a.hold }}</span>
        </div>
      </div>
      <div class="tl-card">
        <membership-types-table :types="membershipDemoAdminTypes">
          <template #actions>
            <tl-icon-btn tooltip="Edit" icon="mdi-pencil" @click="demoOnly"/>
            <v-menu location="bottom end">
              <template #activator="{props: menu}">
                <v-tooltip text="More actions" location="top">
                  <template #activator="{props: tip}">
                    <button v-bind="{...menu, ...tip}" type="button" class="tl-iconbtn" aria-label="More actions">
                      <v-icon size="17">mdi-dots-vertical</v-icon>
                    </button>
                  </template>
                </v-tooltip>
              </template>
              <v-list density="compact">
                <v-list-item v-for="item in MENU" :key="item.title" @click="demoOnly">
                  <template #prepend><v-icon size="small" :icon="item.icon"/></template>
                  <v-list-item-title>{{ item.title }}</v-list-item-title>
                </v-list-item>
                <v-list-item @click="demoOnly">
                  <template #prepend><v-icon size="small" color="error" icon="mdi-archive-outline"/></template>
                  <v-list-item-title class="text-error">Archive</v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
          </template>
        </membership-types-table>
      </div>
    </div>

    <!-- The membership window, as Profile opens it, on the demo's membership -->
    <tl-dialog v-model="open" :title="picked ? `${picked.membershipTypeName}` : 'Membership'" :max-width="520">
      <template v-if="picked">
        <div class="mds-dlg-head">
          <span class="tl-chip" :class="pickedChip.cls">{{ pickedChip.label }}</span>
          <span class="mds-dlg-club">{{ picked.clubName }}</span>
        </div>
        <div class="tl-label mt-4 mb-1">Pays</div>
        <div class="mds-rows">
          <div class="mds-row"><span class="k">Price</span><span class="v">{{ picked.priceLine }}</span></div>
          <div class="mds-row"><span class="k">Card</span><span class="v">Visa ending 4242</span></div>
          <div v-if="paidByOther" class="mds-row"><span class="k">Who pays</span><span class="v">{{ picked.payerName }}</span></div>
        </div>
        <div class="tl-label mt-4 mb-1">{{ pickedIsPass ? 'This block' : 'This term' }}</div>
        <div class="mds-rows">
          <div class="mds-row"><span class="k">Dates</span><span class="v">{{ picked.datesLine }}</span></div>
        </div>
        <div class="mds-switch">
          <div>
            <div class="mds-switch-title">Renew automatically</div>
            <div class="mds-switch-sub">{{ renew ? 'Charged when the term ends, with a reminder before.' : 'Not renewed: it ends with the term.' }}</div>
          </div>
          <v-switch v-model="renew" color="primary" hide-details density="compact" inset/>
        </div>
        <template v-if="!cancelled">
          <div class="tl-label mt-4 mb-1">If you cancel</div>
          <p class="mds-p">Full refund if you cancel within 14 days of starting. After that you keep it until the end of the term.</p>
        </template>
        <p v-else class="mds-p mt-4">Cancelled: you keep it until the end of the term and it isn't renewed. Resume to renew as before.</p>
      </template>
      <template #actions>
        <div class="mds-dlg-actions">
          <v-btn v-if="cancelled" color="primary" variant="flat" @click="resume">Resume {{ noun }}</v-btn>
          <v-btn v-else-if="!paidByOther" color="primary" variant="flat" @click="demoOnly">Ask someone else to pay</v-btn>
          <v-btn variant="outlined" @click="open = false">Close</v-btn>
          <v-btn v-if="!cancelled" color="error" variant="outlined" @click="renew = false">Cancel {{ noun }}</v-btn>
        </div>
      </template>
    </tl-dialog>
  </div>
</template>

<style scoped lang="scss">
.mds-controls {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px 16px;
  margin-bottom: 16px;
}

.mds-hint {
  font-size: 12.5px;
  line-height: 1.5;
  color: rgb(var(--v-theme-textSubtle));
}

.mds-grid {
  display: grid;
  gap: 16px;
  align-items: start;

  @media (min-width: 900px) {
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  }
}

.mds-col {
  display: grid;
  gap: 16px;
  min-width: 0;
}

.mds-frame {
  padding: 14px 16px 16px;

  @media (max-width: 599.98px) {
    padding: 10px;
  }
}

.mds-frame-head {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 4px 10px;
  margin-bottom: 8px;
}

.mds-frame-title {
  font-size: 14px;
  font-weight: 800;
  letter-spacing: -0.2px;
}

.mds-frame-sub {
  font-size: 12px;
  color: rgb(var(--v-theme-textSubtle));
}

.mds-label {
  margin: 10px 0 4px;
}

.mds-list {
  border: 1px solid rgb(var(--v-theme-borderLight));
  border-radius: 12px;
  overflow: hidden;
  background: rgb(var(--v-theme-surface));
}

/* the gate card and the pass offer link into the product; on the demo they are to look at */
.mds-inert :deep(a),
.mds-inert :deep(button) {
  pointer-events: none;
}

.mds-admin {
  display: grid;
  gap: 14px;
}

.mds-waiting {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 14px;
  border-left: 4px solid rgb(var(--v-theme-warning));
}

.mds-waiting-ic {
  width: 38px;
  height: 38px;
  border-radius: 11px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgb(var(--v-theme-light));
  color: rgb(var(--v-theme-warning));
}

.mds-waiting-title {
  font-size: 14px;
  font-weight: 700;
}

.mds-waiting-sub {
  font-size: 12.5px;
  line-height: 1.45;
  color: rgb(var(--v-theme-textSubtle));
}

.mds-apps {
  padding: 0;
  overflow: hidden;
}

.mds-app {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 14px;
  border-top: 1px solid rgb(var(--v-theme-borderLight));

  &:first-child {
    border-top: 0;
  }
}

.mds-app-ic {
  width: 32px;
  height: 32px;
  border-radius: 10px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgb(var(--v-theme-light));
  color: rgb(var(--v-theme-charcoal));
}

.mds-app-title {
  font-size: 13.5px;
  font-weight: 700;
}

.mds-app-sub {
  font-size: 12px;
  color: rgb(var(--v-theme-textSubtle));
}

.mds-dlg-head {
  display: flex;
  align-items: center;
  gap: 10px;
}

.mds-dlg-club {
  font-size: 13px;
  color: rgb(var(--v-theme-textSubtle));
}

.mds-rows {
  border: 1px solid rgb(var(--v-theme-borderLight));
  border-radius: 12px;
  overflow: hidden;
  background: rgb(var(--v-theme-light));
}

.mds-row {
  display: flex;
  gap: 12px;
  align-items: center;
  padding: 9px 14px;
  font-size: 13px;
  border-top: 1px solid rgb(var(--v-theme-borderLight));

  &:first-child {
    border-top: 0;
  }

  .k {
    width: 90px;
    flex-shrink: 0;
    font-size: 12px;
    font-weight: 600;
    color: rgb(var(--v-theme-textSubtle));
  }

  .v {
    flex: 1;
    line-height: 1.45;
  }
}

.mds-switch {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-top: 16px;
  padding: 10px 14px;
  border: 1px solid rgb(var(--v-theme-borderLight));
  border-radius: 12px;
}

.mds-switch-title {
  font-size: 14px;
  font-weight: 700;
}

.mds-switch-sub {
  font-size: 12px;
  color: rgb(var(--v-theme-textSubtle));
}

.mds-p {
  font-size: 13.5px;
  line-height: 1.55;
  margin: 0;
}

/* the window's buttons: a row on a desktop, stacked full width on a phone (primary first, the red one last) */
.mds-dlg-actions {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 8px;
  width: 100%;

  @media (max-width: 599.98px) {
    flex-direction: column;

    :deep(.v-btn) {
      width: 100%;
    }
  }
}
</style>
